import { useEffect, useRef, useState } from "react";
import type { AssetMeterDto } from "@klantpunt/shared";

/** Kort getal voor de as: 1,2k, 12k. */
const short = (n: number) =>
  n >= 10_000
    ? `${Math.round(n / 1000).toLocaleString("nl-NL")}k`
    : n >= 1000
      ? `${(n / 1000).toLocaleString("nl-NL", { maximumFractionDigits: 1 })}k`
      : n.toLocaleString("nl-NL", { maximumFractionDigits: n < 10 ? 2 : 0 });
const nl = (n: number) => n.toLocaleString("nl-NL", { maximumFractionDigits: 2 });
const fmt = (iso: string) =>
  new Date(`${iso}T12:00:00`).toLocaleDateString("nl-NL", { day: "numeric", month: "short", year: "2-digit" });

function ticks(max: number): number[] {
  if (max <= 0) return [0];
  const raw = max / 3;
  const mag = Math.pow(10, Math.floor(Math.log10(raw)));
  const step = [1, 2, 2.5, 5, 10].map((m) => m * mag).find((s) => s >= raw)!;
  return Array.from({ length: Math.ceil(max / step) + 1 }, (_, k) => k * step);
}

/**
 * Verbruik per dag per periode (docs/METERSTANDEN.md §7): staaf per periode, met ernaast dezelfde
 * periode een jaar eerder in neutraal grijs. Hover/focus toont de waarden; de lijst met opnames
 * eronder is de tekstversie.
 */
export function MeterChart({ periods, unit }: { periods: AssetMeterDto["periods"]; unit: string }) {
  const data = periods.filter((p) => p.perDay != null).slice(-12);
  const [hover, setHover] = useState<number | null>(null);
  const wrap = useRef<HTMLDivElement>(null);
  const [W, setW] = useState(360);
  useEffect(() => {
    const el = wrap.current;
    if (!el) return;
    const ro = new ResizeObserver(() => setW(Math.max(240, Math.round(el.clientWidth))));
    ro.observe(el);
    return () => ro.disconnect();
  }, []);
  if (data.length < 2) return null;
  const hasLy = data.some((p) => p.lastYearPerDay != null);
  const H = 160;
  const pad = { l: 40, r: 4, t: 8, b: 22 };
  const max = Math.max(0, ...data.map((p) => Math.max(p.perDay ?? 0, p.lastYearPerDay ?? 0)));
  const tk = ticks(max);
  const top = tk.at(-1) || 1;
  const iw = W - pad.l - pad.r;
  const ih = H - pad.t - pad.b;
  const slot = iw / data.length;
  const bw = Math.min(hasLy ? 12 : 22, slot * (hasLy ? 0.32 : 0.6));
  const every = Math.max(1, Math.ceil(56 / slot));
  const yOf = (v: number) => pad.t + ih - (v / top) * ih;
  const h = hover != null ? data[hover] : null;
  return (
    <figure className="bchart meterchart">
      <figcaption className="small muted">Verbruik per dag ({unit || "eenheid"})</figcaption>
      {hasLy && (
        <ul className="bchart-legend" aria-label="Legenda">
          <li>
            <i className="wt mc-now" aria-hidden="true" />
            Periode
          </li>
          <li>
            <i className="wt mc-ly" aria-hidden="true" />
            Jaar eerder
          </li>
        </ul>
      )}
      <div className="bchart-wrap" ref={wrap}>
        <svg viewBox={`0 0 ${W} ${H}`} role="img" aria-label="Verbruik per dag per periode">
          {tk.map((v) => (
            <g key={v}>
              <line x1={pad.l} x2={W - pad.r} y1={yOf(v)} y2={yOf(v)} className="bchart-grid" />
              <text x={pad.l - 6} y={yOf(v)} className="bchart-axis" textAnchor="end" dominantBaseline="middle">
                {short(v)}
              </text>
            </g>
          ))}
          {data.map((p, k) => {
            const cx = pad.l + k * slot + slot / 2;
            const xNow = hasLy ? cx + 1 : cx - bw / 2;
            const label = `${fmt(p.from)} – ${fmt(p.to)}: ${nl(p.perDay!)} per dag${p.lastYearPerDay != null ? `, jaar eerder ${nl(p.lastYearPerDay)}` : ""}`;
            return (
              <g
                key={p.readingId}
                tabIndex={0}
                role="img"
                aria-label={label}
                className={`bchart-bar${hover === k ? " on" : ""}`}
                onMouseEnter={() => setHover(k)}
                onMouseLeave={() => setHover(null)}
                onFocus={() => setHover(k)}
                onBlur={() => setHover(null)}
              >
                <rect x={pad.l + k * slot} y={pad.t} width={slot} height={ih} className="bchart-hit" />
                {p.lastYearPerDay != null && (
                  <rect
                    x={cx - 1 - bw}
                    y={yOf(p.lastYearPerDay)}
                    width={bw}
                    height={Math.max(0, yOf(0) - yOf(p.lastYearPerDay))}
                    rx={2}
                    className="mc-ly bchart-seg"
                  />
                )}
                <rect
                  x={xNow}
                  y={yOf(p.perDay!)}
                  width={bw}
                  height={Math.max(0, yOf(0) - yOf(p.perDay!))}
                  rx={2}
                  className="mc-now bchart-seg"
                />
                {k % every === 0 && (
                  <text x={cx} y={H - 6} className="bchart-axis" textAnchor="middle">
                    {fmt(p.to)}
                  </text>
                )}
              </g>
            );
          })}
          <line x1={pad.l} x2={W - pad.r} y1={yOf(0)} y2={yOf(0)} className="bchart-base" />
        </svg>
        {h && (
          <div className="bchart-tip" role="status" style={{ left: `${((pad.l + (hover! + 0.5) * slot) / W) * 100}%` }}>
            <strong>
              {fmt(h.from)} – {fmt(h.to)}
            </strong>
            <span className="block small">
              {nl(h.perDay!)} {unit} per dag · {nl(h.consumption ?? 0)} in {h.days} dagen
            </span>
            {h.lastYearPerDay != null && (
              <span className="block small">
                Jaar eerder {nl(h.lastYearPerDay)} per dag ({h.perDay! >= h.lastYearPerDay ? "+" : ""}
                {Math.round((h.perDay! / h.lastYearPerDay - 1) * 100)}%)
              </span>
            )}
          </div>
        )}
      </div>
    </figure>
  );
}
